import {
  ActionIcon,
  Button,
  Divider,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { speechFormats } from '@legacy-shared/texml/speechFormats';
import type { CrmColumnConfig, CrmFieldMapping } from './crm-config';

export type CrmField = { name: string; type: string };

type CrmColumnFieldsProps = {
  config: CrmColumnConfig;
  onConfigChange: (config: CrmColumnConfig) => void;
  tables: string[];
  fields: CrmField[] | null;
  isLoadingTables: boolean;
  isLoadingFields: boolean;
};

function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function toCamelCase(value: string) {
  if (/^[a-z][a-zA-Z0-9]*$/.test(value)) return value;

  const words =
    /^[A-Z_]+$/.test(value) || /^[a-z_]+$/.test(value)
      ? value.toLowerCase().split('_').filter(Boolean)
      : value
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .split(/[\s_]+/)
          .filter(Boolean)
          .map((w) => w.toLowerCase());

  return words.map((w, i) => (i === 0 ? w : capitalize(w))).join('');
}

function FieldMappingRow({
  mapping,
  fieldOptions,
  usedCrmFields,
  fieldsLoaded,
  onChange,
  onRemove,
}: {
  mapping: CrmFieldMapping;
  fieldOptions: { value: string; label: string }[];
  usedCrmFields: Set<string>;
  fieldsLoaded: boolean;
  onChange: (mapping: CrmFieldMapping) => void;
  onRemove: () => void;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Select
          variant="unstyled"
          placeholder="Select field"
          data={fieldOptions.map((f) => ({
            ...f,
            disabled: f.value !== mapping.crmField && usedCrmFields.has(f.value),
          }))}
          value={mapping.crmField || null}
          onChange={(v) =>
            onChange({
              ...mapping,
              crmField: v ?? '',
              variableName: v && !mapping.variableName ? toCamelCase(v) : mapping.variableName,
            })
          }
          searchable
          disabled={!fieldsLoaded}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          variant="unstyled"
          placeholder="customerFirstName"
          value={mapping.variableName}
          styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
          onChange={(e) => onChange({ ...mapping, variableName: e.currentTarget.value })}
        />
      </Table.Td>
      <Table.Td>
        <Select
          variant="unstyled"
          placeholder="None"
          data={speechFormats.map((f) => ({ value: f.id, label: f.label }))}
          value={mapping.speechFormat ?? null}
          onChange={(v) => onChange({ ...mapping, speechFormat: v ?? undefined })}
          clearable
          searchable
        />
      </Table.Td>
      <Table.Td>
        <ActionIcon variant="subtle" color="gray" onClick={onRemove}>
          <IconTrash size={14} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
}

export function CrmColumnFields({
  config,
  onConfigChange,
  tables,
  fields,
  isLoadingTables,
  isLoadingFields,
}: CrmColumnFieldsProps) {
  const fieldOptions = (fields ?? []).map((f) => ({ value: f.name, label: `${f.name} (${f.type})` }));
  const usedCrmFields = new Set(config.fieldMappings.map((m) => m.crmField));

  const replaceMapping = (index: number, mapping: CrmFieldMapping) =>
    onConfigChange({ ...config, fieldMappings: config.fieldMappings.map((m, i) => (i === index ? mapping : m)) });

  return (
    <>
      <Divider label="Table Configuration" labelPosition="left" />

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select
          label="Table Name"
          placeholder={isLoadingTables ? 'Loading tables...' : 'Select a table'}
          data={tables.map((t) => ({ value: t, label: t }))}
          value={config.tableName || null}
          onChange={(v) => onConfigChange({ ...config, tableName: v ?? '' })}
          searchable
          clearable
          rightSection={isLoadingTables ? <Loader size={14} /> : undefined}
          disabled={isLoadingTables}
        />
        <Select
          label="Customer ID Column"
          placeholder={isLoadingFields ? 'Loading fields...' : 'Select a column'}
          data={fieldOptions}
          value={config.customerIdColumn || null}
          onChange={(v) => onConfigChange({ ...config, customerIdColumn: v ?? '' })}
          searchable
          clearable
          rightSection={isLoadingFields ? <Loader size={14} /> : undefined}
          disabled={isLoadingFields || !fields}
        />
      </SimpleGrid>

      <NumberInput
        label="Customer ID Digit Length"
        description="Expected number of digits in customer ID"
        value={config.customerIdDigitLength}
        onChange={(v) => onConfigChange({ ...config, customerIdDigitLength: typeof v === 'number' ? v : 6 })}
        min={1}
        max={20}
        w={200}
      />

      <Divider label="Phone Matching" labelPosition="left" />

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select
          label="Phone Match Column"
          placeholder={isLoadingFields ? 'Loading fields...' : 'Select a column'}
          data={fieldOptions}
          value={config.phoneMatchColumn || null}
          onChange={(v) => onConfigChange({ ...config, phoneMatchColumn: v ?? '' })}
          searchable
          rightSection={isLoadingFields ? <Loader size={14} /> : undefined}
          disabled={isLoadingFields || !fields}
        />
        <MultiSelect
          label="Caller ID Match Columns"
          placeholder={isLoadingFields ? 'Loading fields...' : 'Select columns'}
          data={fieldOptions}
          value={config.callerIdMatchColumn}
          onChange={(v) => onConfigChange({ ...config, callerIdMatchColumn: v })}
          searchable
          rightSection={isLoadingFields ? <Loader size={14} /> : undefined}
          disabled={isLoadingFields || !fields}
        />
      </SimpleGrid>

      <MultiSelect
        label="Phone Update Columns"
        description="When a customer ID is matched, the caller's phone number is saved to the first blank column in this list. These columns are also checked during caller-ID matching on future calls, so next time the same caller dials in they are routed down the Match leg automatically."
        placeholder={isLoadingFields ? 'Loading fields...' : 'Select columns in priority order'}
        data={fieldOptions}
        value={config.phoneUpdateColumns ?? []}
        onChange={(v) => onConfigChange({ ...config, phoneUpdateColumns: v.length > 0 ? v : undefined })}
        searchable
        rightSection={isLoadingFields ? <Loader size={14} /> : undefined}
        disabled={isLoadingFields || !fields}
      />

      <Divider label="Field Mappings" labelPosition="left" />

      <Text size="sm" c="dimmed">
        Map CRM fields to flow variables available during calls.
      </Text>

      {config.fieldMappings.length > 0 && (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>CRM Field</Table.Th>
              <Table.Th>Variable Name</Table.Th>
              <Table.Th>Speech Format</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {config.fieldMappings.map((mapping, index) => (
              <FieldMappingRow
                key={index}
                mapping={mapping}
                fieldOptions={fieldOptions}
                usedCrmFields={usedCrmFields}
                fieldsLoaded={fields !== null}
                onChange={(updated) => replaceMapping(index, updated)}
                onRemove={() =>
                  onConfigChange({ ...config, fieldMappings: config.fieldMappings.filter((_, i) => i !== index) })
                }
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      <div>
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            onConfigChange({
              ...config,
              fieldMappings: [...config.fieldMappings, { crmField: '', variableName: '' }],
            })
          }
        >
          Add Mapping
        </Button>
      </div>
    </>
  );
}
